// @ts-nocheck
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getShouldLoadInventory, getShouldLoadPricing } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    chain,
    PrepareStructuredPageData as BasePrepareStructuredPageData,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { createChainHandler, replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

const PrepareStructuredPageData = createChainHandler("PrepareStructuredPageData", chain, async props => {
    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        manufacturerItem,
        detail,
        brand: productBrand,
        content,
        // Store Custom Code
        productNumber,
        attributeTypes,
    } = props.parameter.product;
    const state = props.getState();

    if (content === undefined) {
        /**
         * sometimes content may not be loaded because product was initially loaded for product carousel
         * in this case we should not set structured data and wait until product with all data is loaded
         */
        return false;
    }

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        productID: id,
        name: productTitle,
        image: [...new Set([smallImagePath, mediumImagePath, largeImagePath])], // Store Custom Code - remove duplicates if image paths are the same
        mpn: manufacturerItem,
        sku: productNumber ?? detail?.sku, // Store Custom Code
        url: canonicalUrl,
    } as any;

    if (content?.htmlContent) {
        data.description = content.htmlContent.replace(/<[^>]+>/g, "").replace(/[\r\n\t]/g, "");
    }

    const productInfo = props.parameter.productInfo;
    if (
        productInfo &&
        !props.parameter.product.quoteRequired &&
        getShouldLoadPricing(state) &&
        getShouldLoadInventory(state)
    ) {
        await waitFor(() => !!productInfo.pricing && !!productInfo.inventory);

        const { pricing, inventory, unitOfMeasure } = productInfo;

        if (pricing && inventory && pricing.unitNetPrice > 0) {
            const availability = inventory?.inventoryAvailabilityDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.availability;

            data.offers = {
                "@type": "Offer",
                availability:
                    availability?.messageType !== 2
                        ? availability?.messageType === 3
                            ? "https://schema.org/LimitedAvailability"
                            : "https://schema.org/InStock"
                        : "https://schema.org/OutOfStock",
                price: pricing.unitNetPrice,
                priceCurrency: props.parameter.session?.currency?.currencyCode,
                url: canonicalUrl,
            };
        }
    }

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            data.brand = {
                "@type": "Brand",
                identifier: brand.id,
                name: brand.name,
                url: brand.detailPagePath,
            };
        }
    }

    if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
        const category = getCategoryState(
            state,
            props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
        ).value;

        if (category) {
            data.category = category.name ?? category.urlSegment; // Store Custom Code
        }
    }

    /* Store Custom Code - Start */
    const attributeTypesData: any[] = [];

    if (attributeTypes && attributeTypes?.length > 0) {
        attributeTypes.forEach(attributeType => {
            attributeType.attributeValues?.forEach(attributeValue => {
                attributeTypesData.push({
                    "@type": "PropertyValue",
                    name: attributeType.label ?? attributeType.name,
                    value: attributeValue.valueDisplay ?? attributeValue.value,
                });
            });
        });

        data.additionalProperty = attributeTypesData;
    }
    /* Store Custom Code - End */

    props.result = { structuredPageData: JSON.stringify(data) };
});

replaceInChain(chain, BasePrepareStructuredPageData, PrepareStructuredPageData);
